/**
 * The saved call-script library of the dialer (CallScripts, AIAssistant).
 *
 * Stored as CallerScript rows of the organisation, titled "library:<name>".
 * It used to live in AdminSettings under global keys
 * `call_script_<org email>_<name>`: a platform table with no tenant, which
 * only a super admin may use since AUDIT-F01/F02 (callers were already
 * refused). CallerScript is tenant-scoped by the server, so the library is
 * shared by the members of one organisation and invisible to the others.
 * Scripts saved before the move are copied here by server migration 021
 * (call-scripts-to-caller-script) when their organisation is unambiguous.
 *
 * ScriptPanel stores its own pitch in the same entity (title "panel"); it
 * skips library rows with isLibraryScript().
 */
import { api } from '@/api/api-client';

export const LIBRARY_PREFIX = 'library:';

export interface LibraryScript {
  id: string;
  name: string;
  content: string;
  description: string;
}

interface CallerScriptRow {
  id: string;
  title?: string;
  content?: string;
}

export function isLibraryScript(doc: { title?: unknown } | null | undefined): boolean {
  return typeof doc?.title === 'string' && doc.title.startsWith(LIBRARY_PREFIX);
}

export async function listLibraryScripts(): Promise<LibraryScript[]> {
  const rows = await api.entities.CallerScript.filter<CallerScriptRow>({ title: { $regex: `^${LIBRARY_PREFIX}` } });
  return rows.filter(isLibraryScript).map((row) => ({
    id: row.id,
    name: (row.title ?? '').slice(LIBRARY_PREFIX.length),
    content: row.content ?? '',
    // CallerScript has no description field; AdminSettings never stored one either.
    description: '',
  }));
}

export function saveLibraryScript(input: { id?: string; name: string; content: string; aiGenerated?: boolean }) {
  const payload = {
    title: `${LIBRARY_PREFIX}${input.name.trim()}`,
    content: input.content,
    is_ai_generated: input.aiGenerated === true,
    customer_id: '',
  };
  return input.id ? api.entities.CallerScript.update(input.id, payload) : api.entities.CallerScript.create(payload);
}

export function deleteLibraryScript(id: string) {
  return api.entities.CallerScript.delete(id);
}
